import type {
  StreamData,
  StreamMetrics,
  StreamFormat,
  TextFragment,
  AudioEventTextData,
  SpeakerTextData,
  AudioData,
} from "../types/model.types";
import {
  isLiveFormat,
  isTextStreamFormat,
  isAudioStreamFormat,
} from "../utils/type-helpers";

export interface StreamQueueCallbacks {
  onAudioDataProcess: (audioData: AudioData) => void;
  onAudioEventProcess: (event: string) => void;
  onTextFragmentProcess: (textFragment: TextFragment) => void;
  onStreamMetricsProcess: (metrics: StreamMetrics) => void;
}

export interface StreamQueueOptions {
  callbacks: StreamQueueCallbacks;
}

/**
 * Manages queues for stream data and text fragments with timing control
 * Handles all queue-related processing internally through callbacks
 */
export class StreamQueue {
  private _callbacks: StreamQueueCallbacks;
  private _format: StreamFormat | null = null;

  // Stream data queue
  private _streamDataQueue: StreamData[] = [];
  private _streamDataDequeueTimer: ReturnType<typeof setTimeout> | null = null;
  private _queueWaitingForPlayback: boolean = false;

  // Text fragment queue
  private _textFragmentQueue: TextFragment[] = [];
  private _textFragmentDequeueTimer: ReturnType<typeof setTimeout> | null =
    null;

  // Background sync tracking (for iOS transcript catch-up)
  private _lastDequeueTime: number = 0;
  private _isBackgrounded: boolean = false;

  constructor(options: StreamQueueOptions) {
    this._callbacks = options.callbacks;
    this._setupVisibilityHandler();
  }

  /**
   * Sets up visibility change listener for iOS background sync
   */
  private _setupVisibilityHandler(): void {
    if (typeof document === "undefined") {
      return;
    }

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this._onBackgrounded();
      } else if (this._isBackgrounded) {
        this._onForegrounded();
      }
    });
  }

  /**
   * Called when page becomes hidden. Pauses text timing.
   */
  private _onBackgrounded(): void {
    this._isBackgrounded = true;
    this._lastDequeueTime = Date.now();

    // Clear active text fragment timer to avoid race conditions
    if (this._textFragmentDequeueTimer !== null) {
      clearTimeout(this._textFragmentDequeueTimer);
      this._textFragmentDequeueTimer = null;
    }
  }

  /**
   * Called when page becomes visible again. Catches up the text queue.
   */
  private _onForegrounded(): void {
    this._isBackgrounded = false;

    if (this._textFragmentQueue.length === 0) {
      return;
    }

    const elapsedMs = Date.now() - this._lastDequeueTime;
    let accumulatedDurationMs = 0;

    // Skip fragments whose display time has passed
    while (this._textFragmentQueue.length > 0) {
      const fragment = this._textFragmentQueue[0];
      const fragmentDurationMs = fragment.duration * 1000;

      if (accumulatedDurationMs + fragmentDurationMs <= elapsedMs) {
        // This fragment's time has fully passed — emit it and continue
        const skippedFragment = this._textFragmentQueue.shift()!;
        this._callbacks.onTextFragmentProcess(skippedFragment);
        accumulatedDurationMs += fragmentDurationMs;
      } else {
        // This fragment is partially elapsed or not yet due
        break;
      }
    }

    // Resume normal dequeuing if fragments remain
    if (
      this._textFragmentQueue.length > 0 &&
      this._textFragmentDequeueTimer === null
    ) {
      // Calculate remaining time for the current fragment
      const remainingMs = Math.max(
        0,
        accumulatedDurationMs +
          this._textFragmentQueue[0].duration * 1000 -
          elapsedMs,
      );

      this._textFragmentDequeueTimer = setTimeout(() => {
        this._dequeueTextFragment();
      }, remainingMs);
    }
  }

  /**
   * Sets the stream format for processing decisions
   */
  public setFormat(format: StreamFormat): void {
    this._format = format;
  }

  /**
   * Adds stream data to the queue for processing
   * This is the main public method - all processing happens through callbacks
   */
  public enqueueStreamData(streamData: StreamData): void {
    if (this._format && isLiveFormat(this._format)) {
      // Process live format immediately
      this._processLiveStreamData(streamData);
    } else {
      // Queue non-live format for timed processing
      if (streamData.isEndOfSentence ?? false) {
        this._clearMinStartTimes();
      }
      this._streamDataQueue.push(streamData);

      if (
        this._streamDataDequeueTimer === null &&
        !this._queueWaitingForPlayback
      ) {
        this._dequeueStreamData();
      }
    }
  }

  /**
   * Notifies the queue manager that playback has finished
   */
  public onPlaybackFinished(): void {
    this._queueWaitingForPlayback = false;
    this._dequeueStreamData();
  }

  /**
   * Clears all queues and timers
   */
  public clear(): void {
    // Clear stream data queue
    this._streamDataQueue = [];
    if (this._streamDataDequeueTimer !== null) {
      clearTimeout(this._streamDataDequeueTimer);
      this._streamDataDequeueTimer = null;
    }
    this._queueWaitingForPlayback = false;

    // Clear text fragment queue
    this._textFragmentQueue = [];
    if (this._textFragmentDequeueTimer !== null) {
      clearTimeout(this._textFragmentDequeueTimer);
      this._textFragmentDequeueTimer = null;
    }

    // Reset background sync state
    this._isBackgrounded = false;
    this._lastDequeueTime = 0;
  }

  /**
   * Gets the current queue sizes
   */
  public getQueueSizes(): { streamData: number; textFragments: number } {
    return {
      streamData: this._streamDataQueue.length,
      textFragments: this._textFragmentQueue.length,
    };
  }

  /**
   * Processes live format stream data immediately
   */
  private _processLiveStreamData(streamData: StreamData): void {
    if (
      this._format &&
      isAudioStreamFormat(this._format) &&
      streamData.audioStreamData
    ) {
      this._callbacks.onAudioDataProcess(streamData.audioStreamData);
    }

    if (
      this._format &&
      isTextStreamFormat(this._format) &&
      streamData.textStreamData
    ) {
      this._processTextStreamData(streamData.textStreamData);
    }
  }

  /**
   * Processes queued stream data with timing control
   */
  private _processQueuedStreamData(streamData: StreamData): void {
    if (streamData.serverIngestTimeMs && streamData.receiveTime) {
      // Calculate delay using receive time (when data was received) not processing time
      const delay = streamData.receiveTime - streamData.serverIngestTimeMs;
      this._callbacks.onStreamMetricsProcess({ delay });
    }
    if (streamData.audioStreamData) {
      this._processQueuedAudioStreamData(streamData.audioStreamData);
    }

    if (streamData.textStreamData) {
      this._processTextStreamData(streamData.textStreamData);
    }
  }

  /**
   * Processes audio stream data
   */
  private _processQueuedAudioStreamData(audioData: AudioData): void {
    // Process buffer immediately
    this._callbacks.onAudioDataProcess(audioData);
  }

  /**
   * Processes text stream data and handles different types
   */
  private _processTextStreamData(
    textStreamData: AudioEventTextData | SpeakerTextData,
  ): void {
    if (textStreamData.type === "audioEvent") {
      this._callbacks.onAudioEventProcess(textStreamData.text);
    } else if (textStreamData.type === "speech") {
      this._enqueueTextFragments(textStreamData.translationStreamFragments);
    }
  }

  /**
   * Adds text fragments to the queue for timed processing
   */
  private _enqueueTextFragments(textFragments: TextFragment[]): void {
    this._textFragmentQueue.push(...textFragments);

    if (this._textFragmentDequeueTimer === null) {
      this._dequeueTextFragment();
    }
  }

  /**
   * Clears minimum start times from all queued stream data
   */
  private _clearMinStartTimes(): void {
    this._streamDataQueue.forEach((streamData) => {
      if (streamData.audioStreamData && streamData.minStartTime) {
        streamData.minStartTime = null;
      }
    });

    if (this._streamDataDequeueTimer !== null) {
      clearTimeout(this._streamDataDequeueTimer);
      this._dequeueStreamData();
    }
  }

  /**
   * Dequeues and processes stream data with timing control
   */
  private _dequeueStreamData(): void {
    this._streamDataDequeueTimer = null;

    const streamData = this._streamDataQueue.shift();
    if (!streamData) {
      return;
    }

    if (
      streamData.audioStreamData &&
      streamData.minStartTime &&
      streamData.minStartTime.getTime() > Date.now()
    ) {
      // Schedule processing for later
      const delay = streamData.minStartTime.getTime() - Date.now();
      this._streamDataDequeueTimer = setTimeout(() => {
        this._dequeueStreamData();
      }, delay);
    } else {
      // Process immediately
      this._queueWaitingForPlayback = true;
      this._processQueuedStreamData(streamData);
    }
  }

  /**
   * Dequeues and processes text fragments with timing
   */
  private _dequeueTextFragment(): void {
    this._textFragmentDequeueTimer = null;

    const textFragment = this._textFragmentQueue.shift();
    if (!textFragment) {
      return;
    }

    this._callbacks.onTextFragmentProcess(textFragment);

    // Schedule next text fragment (convert duration from seconds to milliseconds)
    this._textFragmentDequeueTimer = setTimeout(() => {
      this._dequeueTextFragment();
    }, textFragment.duration * 1000);
  }
}
